import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';
import { TABS } from '../constants/liveCodeTabs';

const useQueryParamEmbedSettings = ({
  settings,
  selectedTabIndex,
  selectedFile,
  embedComponentType,
  dashboardId,
  setSelectedTabIndex,
  setSelectedFile,
  refreshSettingsAndCodeValues,
  defaultSettings,
  embedComponentTypeOptions,
}) => {
  const history = useHistory();
  const useHashRouter = process.env.REACT_APP_USE_HASH_ROUTER === 'true';

  useEffect(() => {
    const path = history.location.pathname;
    const options = /** @type {Record<string, any>} */ (
      Object.entries(settings).reduce(
        (res, [k, v]) => (defaultSettings[k] !== v ? { ...res, [k]: v } : res),
        {}
      )
    );
    const defaultDashboardId = options.dashboardId;
    if (selectedTabIndex > 0) {
      options.modeTabIndex = selectedTabIndex;
    }
    if (selectedFile.name !== TABS.html.name) {
      options.selectedFileName = selectedFile.name;
    }
    if (embedComponentType) {
      options.embedComponentType = embedComponentType;
    }
    if (embedComponentType === 'dashboard') {
      options.dashboardId = dashboardId;
    } else {
      options.dashboardId = defaultDashboardId;
    }
    const newSearch = queryString.stringify(options);
    if (useHashRouter) {
      const normalizedSearch = newSearch ? `?${newSearch}` : '';
      if (history.location.search !== normalizedSearch) {
        history.replace({
          pathname: history.location.pathname,
          search: normalizedSearch,
        });
      }
      return;
    }

    const isQueryEmpty = newSearch.length === 0;
    window.history.replaceState(null, '', isQueryEmpty ? path : `${path}?${newSearch}`);
  }, [selectedTabIndex, settings, selectedFile, embedComponentType, dashboardId]);

  useEffect(() => {
    const rawSearch = (() => {
      if (history.location.search) {
        return history.location.search;
      }
      if (useHashRouter) {
        const hash = window.location.hash || '';
        const queryIndex = hash.indexOf('?');
        if (queryIndex >= 0) {
          return hash.substring(queryIndex);
        }
      }
      return '';
    })();

    const settings = queryString.parse(rawSearch);
    const parsedSettings = Object.entries(settings).reduce(
      (res, [k, v]) => ({ ...res, [k]: v === 'false' || v === 'true' ? v === 'true' : v }),
      {}
    );

    if (
      settings.modeTabIndex &&
      !Number.isNaN(settings.modeTabIndex) &&
      +settings.modeTabIndex <= 1 &&
      +settings.modeTabIndex >= 0
    ) {
      setSelectedTabIndex(+settings.modeTabIndex);
    }
    if (settings.selectedFileName) {
      const fileFromParams = Object.values(TABS).find(({ name }) => name === settings.selectedFileName);
      if (fileFromParams) {
        setSelectedFile(fileFromParams);
      }
    }
    const componentType = embedComponentTypeOptions.some((o) => o === settings.embedComponentType)
      ? settings.embedComponentType
      : embedComponentTypeOptions[0];
    refreshSettingsAndCodeValues(parsedSettings, componentType, settings.dashboardId);
  }, []);
};

export default useQueryParamEmbedSettings;
